import { useState, type MouseEvent } from 'react';
import { Clock, Trash2 } from 'lucide-react';
import type { OnboardedData } from '../api/getComponentList';
import {
  type DeletionMode,
  useDeleteComponent,
} from '../api/deleteComponent';
import DeleteComponentDialog from './DeleteComponentDialog';
import TechnologySymbol from './TechnologySymbol';
import {
  formatCompactNumber,
  formatScanAge,
  getComponentDisplayName,
  getComponentKind,
  getComponentKindLabel,
  getOnboardedTestScriptCount,
  getLineCount,
} from './componentInventoryUtils';
import { filterCardTechnologies } from '@/utils/technologySymbols';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { COMPONENTS_CARD_SHELL } from './components-page-layout';
import CaptureStatusPanel, {
  CaptureStatusFooter,
} from '@/components/smartai/capture/CaptureStatusPanel';
import ComponentDeletedActions from './ComponentDeletedActions';

interface ComponentCardProps {
  repo: OnboardedData;
  handleUserSelectedComponent: (repo: OnboardedData) => void;
}

const MAX_VISIBLE_TECH = 4;

const MetaChip = ({
  prefix,
  value,
  title,
  emphasis,
}: {
  prefix?: string;
  value: string;
  title?: string;
  emphasis?: boolean;
}) => (
  <span
    className={cn(
      'inline-flex shrink-0 min-w-0 max-w-full items-center truncate rounded-full px-2 py-[0.2rem] text-[0.65rem] font-medium',
      emphasis
        ? 'border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text)]'
        : 'border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-secondary)]',
    )}
    title={title ?? value}>
    {prefix ? (
      <span className="mr-1 shrink-0 text-[var(--inv-text-muted)]">{prefix}</span>
    ) : null}
    <span className="min-w-0 truncate">{value}</span>
  </span>
);

const MetricLabel = ({ children }: { children: string }) => (
  <span className="text-[0.5625rem] font-semibold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
    {children}
  </span>
);

const DetailStatsRow = ({
  stats,
  className,
}: {
  stats: { value: string; label: string }[];
  className?: string;
}) => (
  <div className={cn('flex w-full items-end', className)}>
    {stats.map((stat, index) => (
      <div
        key={stat.label}
        className={cn(
          'flex min-w-0 flex-1 flex-col items-start justify-end gap-1',
          index > 0 && 'border-l border-[var(--inv-border-soft)] pl-3',
          index === 0 && 'pr-2',
          index > 0 && index < stats.length - 1 && 'pr-2',
        )}>
        <span className="text-[1rem] font-bold tabular-nums leading-none tracking-[-0.02em] text-[var(--inv-text)]">
          {stat.value}
        </span>
        <span className="text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
          {stat.label}
        </span>
      </div>
    ))}
  </div>
);

const ComponentCard = ({
  repo,
  handleUserSelectedComponent,
}: ComponentCardProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { mutate: deleteComponent, isPending: isDeleting } =
    useDeleteComponent();

  const name = getComponentDisplayName(repo);
  const kind = getComponentKind(repo);
  const hasScan = Boolean(repo.lastScanAt?.trim());
  const lineCoverage = Math.round(repo.metrics?.linesOfCodeCoverage ?? 0);
  const methodCoverage = Math.round(repo.metrics?.inventoryModeCoverage ?? 0);
  const technologies = filterCardTechnologies(
    repo.detectedTechnologies?.filter(Boolean) ?? [],
    kind,
  );
  const visibleTech = technologies.slice(0, MAX_VISIBLE_TECH);
  const hiddenTechCount = Math.max(technologies.length - MAX_VISIBLE_TECH, 0);

  const files = repo.inventory?.files ?? 0;
  const lines = getLineCount(repo);
  const methods = repo.inventory?.methods ?? 0;
  const classes = repo.inventory?.classes ?? 0;
  const tests = getOnboardedTestScriptCount(repo);
  const testCaseValue =
    !hasScan && tests === 0 ? '—' : formatCompactNumber(tests);

  const detailStats = [
    { value: formatCompactNumber(files), label: 'Files' },
    { value: formatCompactNumber(lines), label: 'Lines' },
    { value: formatCompactNumber(methods), label: 'Methods' },
    { value: formatCompactNumber(classes), label: 'Classes' },
    { value: testCaseValue, label: 'Scripts' },
  ];

  const pendingDeletion =
    repo.pendingDeletion?.branchId === repo.branchId
      ? repo.pendingDeletion
      : null;
  const isPendingDeletion =
    Boolean(pendingDeletion) && pendingDeletion?.status !== 'failed';
  const isSoftDeleted = Boolean(repo.isDeleted);

  /** True only when QTrace actually raised an instrumentation PR for this repo. */
  const willAwaitDeinstrumentPr = (() => {
    const prStatus = String(repo.prStatus || '').toUpperCase();
    if (prStatus === 'NOT_REQUIRED') return false;
    const appPr = repo.instrumentationPr;
    const testPr = repo.testInstrumentationPr;
    const hasPr = (p?: OnboardedData['instrumentationPr']) =>
      Boolean(p?.prId) ||
      ['raised', 'open', 'merged', 'approved'].includes(
        String(p?.status || '').toLowerCase(),
      );
    return hasPr(appPr) || hasPr(testPr);
  })();

  const statusMessage = isPendingDeletion
    ? 'Pending deletion — awaiting de-instrumentation PR merge'
    : isSoftDeleted
      ? 'Deleted — restore to access again'
      : null;

  const lineDisplay = !hasScan ? '—' : `${lineCoverage}%`;
  const methodDisplay = !hasScan ? '—' : `${methodCoverage}%`;

  const handleDeleteClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isPendingDeletion) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = (deletionMode: DeletionMode) => {
    deleteComponent(
      { branchId: repo.branchId, deletionMode },
      {
        onSuccess: () => setShowDeleteModal(false),
        onError: err => {
          console.error('[deleteComponent]', err);
          window.alert(
            err?.message ||
              'Delete failed. Check the API/backend logs and try again.',
          );
        },
      },
    );
  };

  return (
    <>
      <article
        role={isSoftDeleted ? undefined : 'button'}
        tabIndex={isSoftDeleted ? -1 : 0}
        aria-label={isSoftDeleted ? `${name} (deleted)` : `Open ${name}`}
        aria-disabled={isSoftDeleted || undefined}
        onClick={() => {
          if (!isSoftDeleted) handleUserSelectedComponent(repo);
        }}
        onKeyDown={e => {
          if (isSoftDeleted) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleUserSelectedComponent(repo);
          }
        }}
        className={cn(
          COMPONENTS_CARD_SHELL,
          'group relative flex h-full flex-col overflow-hidden outline-none',
          'rounded-2xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]',
          'shadow-[var(--inv-shadow)]',
          isSoftDeleted
            ? 'cursor-default opacity-55 saturate-[0.65]'
            : cn(
                'cursor-pointer',
                'transition-[transform,box-shadow,border-color] duration-200 ease-out',
                'hover:-translate-y-0.5 hover:border-[var(--inv-border-strong)] hover:shadow-[var(--inv-shadow-hover)]',
                'focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)]',
              ),
        )}>
        {/* Capture corner strip (always) + hover labels — no-op when capture disabled */}
        {!isSoftDeleted ? <CaptureStatusPanel repo={repo} /> : null}

        {isSoftDeleted ? (
          <ComponentDeletedActions
            branchId={repo.branchId}
            name={name}
            className="absolute left-auto right-3 top-3 z-20 opacity-100"
          />
        ) : null}

        {!repo?.isView && !isPendingDeletion && !isSoftDeleted && (
          <Button
            type="button"
            variant="icon"
            className={cn(
              'absolute left-auto right-3 top-[3.25rem] z-20 h-7 w-7 shrink-0 rounded-full p-0',
              'border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]',
              'opacity-0 transition-[opacity,background-color,border-color] duration-150',
              'hover:border-[#fecdca]',
              'focus-visible:opacity-100 focus-visible:ring-[#fecdca]',
              'group-hover:opacity-100 group-focus-within:opacity-100',
            )}
            aria-label={`Delete ${name}`}
            onClick={handleDeleteClick}>
            <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
          </Button>
        )}

        {/* Header — fixed zones so cards stay even with long repo/branch names */}
        <div className="flex shrink-0 flex-col gap-0.5 bg-[var(--inv-surface)] px-5 pb-3 pt-4">
          <div className="flex min-h-[1.375rem] min-w-0 items-center gap-2 pr-12">
            <h3
              className="m-0 min-w-0 flex-1 truncate font-sans text-[0.9375rem] font-semibold leading-tight tracking-[-0.01em] text-[var(--inv-text)]"
              title={name}>
              {name}
            </h3>
            <span className="shrink-0 rounded-full bg-[var(--inv-surface-muted)] px-2 py-[0.18rem] text-[0.60rem] font-bold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
              {getComponentKindLabel(repo)}
            </span>
          </div>

          <p
            className={cn(
              'm-0 min-h-[1.125rem] truncate text-[0.75rem] font-medium leading-snug',
              statusMessage
                ? isSoftDeleted
                  ? 'text-[var(--inv-text-muted)]'
                  : 'text-[var(--inv-text-secondary)]'
                : 'invisible',
            )}
            title={statusMessage ?? undefined}>
            {statusMessage ?? 'Placeholder'}
          </p>

          <div className="flex min-h-[2.625rem] flex-wrap content-center items-center gap-1.5">
            {/* <MetaChip prefix="repo" value={repo.repoName} title={repo.repoName} /> */}
            <MetaChip prefix="br" value={repo.branchName} title={repo.branchName} />
          </div>
        </div>

        {/* Metrics — fixed height; default row always present */}
        <div className="relative h-16 shrink-0 bg-[var(--inv-surface)] px-5 pb-3 pt-1">
          <div
            className="flex h-full w-full items-end gap-8 transition-opacity duration-200 group-hover:opacity-0 group-focus-within:opacity-0"
            aria-hidden={hasScan ? undefined : true}>
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[1.25rem] font-bold tabular-nums leading-none tracking-[-0.03em] text-[var(--inv-text)]">
                {lineDisplay}
              </span>
              <MetricLabel>Line</MetricLabel>
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[1.25rem] font-bold tabular-nums leading-none tracking-[-0.03em] text-[var(--inv-text)]">
                {methodDisplay}
              </span>
              <MetricLabel>Method</MetricLabel>
            </div>
            {!hasScan ? (
              <span className="mb-0.5 ml-auto truncate text-[0.6875rem] font-medium text-[var(--inv-text-muted)]">
                Awaiting first scan
              </span>
            ) : null}
          </div>

          {hasScan ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 top-0 z-[1] flex items-end bg-[var(--inv-surface)] px-5 pb-3 opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
              <DetailStatsRow stats={detailStats} />
            </div>
          ) : null}
        </div>

        {/* Capture status line — under stats, above tech-stack footer */}
        {!isSoftDeleted ? <CaptureStatusFooter repo={repo} /> : null}

        {/* Footer — distinct, balanced, uncrowded */}
        <footer className="mt-auto flex h-11 shrink-0 items-center justify-between gap-3 border-t bg-[var(--inv-surface-muted)] px-5">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-[0.75rem] font-medium leading-none text-[var(--inv-text-secondary)]">
            <Clock
              className="h-3.5 w-3.5 shrink-0 text-[var(--inv-text-muted)]"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <span className="truncate">{formatScanAge(repo.lastScanAt)}</span>
          </span>

          <div className="flex shrink-0 items-center gap-3">
            {visibleTech.length > 0 && (
              <div
                className="flex items-center gap-1"
                title={technologies.join(', ')}>
                {visibleTech.map(tech => (
                  <TechnologySymbol key={tech} technology={tech} size="sm" />
                ))}
                {hiddenTechCount > 0 && (
                  <span
                    className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-1.5 text-[0.5625rem] font-semibold text-[var(--inv-text-secondary)]"
                    title={technologies.slice(MAX_VISIBLE_TECH).join(', ')}>
                    +{hiddenTechCount}
                  </span>
                )}
              </div>
            )}

            {/* <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text-secondary)] transition-colors group-hover:border-[var(--inv-accent)] group-hover:bg-[var(--inv-accent)] group-hover:text-[var(--inv-on-accent)] group-focus-within:border-[var(--inv-accent)] group-focus-within:bg-[var(--inv-accent)] group-focus-within:text-[var(--inv-on-accent)]"
              aria-hidden="true">
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
            </span> */}
          </div>
        </footer>
      </article>

      <DeleteComponentDialog
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        componentName={name}
        willAwaitDeinstrumentPr={willAwaitDeinstrumentPr}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ComponentCard;
